<template>
  <v-container fluid>
    <v-card flat class="mb-4">
      <v-card-title>
        <v-icon left>mdi-gauge</v-icon>
        Pump Monitor
      </v-card-title>

      <v-card-title class="py-0">
        <span class="caption grey--text">Live Events</span>
        <v-spacer />
        <v-btn small text @click="clearRecentEvents" :disabled="!recentEvents.length">
          <v-icon left small>mdi-close-circle-outline</v-icon>
          Clear
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-0">
        <v-sheet outlined rounded style="max-height: 220px; overflow-y: auto">
          <v-list dense v-if="recentEvents.length">
            <v-list-item v-for="(event, i) in recentEvents" :key="i">
              <v-list-item-content>
                <v-list-item-title>
                  <span class="grey--text mr-2">{{ formatDate(event.time) }}</span>
                  <span class="font-weight-medium mr-2">{{ event.station }}</span>
                  <span>{{ event.message }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else class="pa-4 text-center grey--text">
            Waiting for incoming data...
          </div>
        </v-sheet>
      </v-card-text>
    </v-card>

    <v-card flat>
      <v-card-title>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Filter in results..."
          single-line
          hide-details
          dense
          outlined
          style="max-width: 300px"
          class="mr-4"
        />
        <v-select
          v-model="selectedType"
          :items="typeOptions"
          item-text="name"
          item-value="_id"
          label="Type"
          clearable
          hide-details
          dense
          outlined
          style="max-width: 220px"
        />
        <v-spacer />
        <span class="caption grey--text">{{ tableItems.length }} pumps</span>
      </v-card-title>

      <v-data-table
        class="sticky-cols"
        :headers="headers"
        :items="tableItems"
        :search="search"
        :loading="loading"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        sort-by="name"
        dense
      >
        <template v-slot:[`item.statusOnline`]="{ item }">
          <v-chip small :color="item.statusOnline === 'Online' ? 'green' : 'red'" dark label>
            {{ item.statusOnline }}
          </v-chip>
        </template>

        <template v-slot:[`item.pump`]="{ item }">
          <v-chip small :color="pumpModeColor(item.pump)" dark label>
            {{ item.pump }}
          </v-chip>
        </template>

        <template v-slot:[`item.engine`]="{ item }">
          <v-icon :color="item.engine ? 'green' : 'grey'">
            {{ item.engine ? "mdi-engine" : "mdi-engine-off" }}
          </v-icon>
        </template>

        <template v-slot:[`item.lastUpdate`]="{ item }">
          {{ formatDate(item.lastUpdate) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "PumpMonitor",
  data() {
    return {
      search: "",
      loading: false,
      selectedType: null,
    };
  },
  computed: {
    ...mapGetters(["allStations", "getRecentEvents", "allTypes"]),
    stations() {
      return this.allStations || [];
    },
    recentEvents() {
      return this.getRecentEvents || [];
    },
    typeOptions() {
      return this.allTypes || [];
    },
    filteredStations() {
      if (!this.selectedType) return this.stations;
      return this.stations.filter((station) => {
        const type = station.type;
        const typeId = type && typeof type === "object" ? type._id : type;
        return typeId === this.selectedType;
      });
    },
    sensorNames() {
      const names = new Set();
      this.filteredStations.forEach((station) => {
        (station.status || []).forEach((s) => {
          if (s && s.name) names.add(s.name);
        });
      });
      return Array.from(names);
    },
    headers() {
      const fixed = [
        { text: "Station", value: "name", width: "180px" },
        { text: "Gateway Serial", value: "gatewaySerial", width: "160px" },
        { text: "Status", value: "statusOnline", width: "110px" },
        { text: "Pump Mode", value: "pump" },
        { text: "Engine", value: "engine", sortable: false },
      ];
      const sensors = this.sensorNames.map((n) => ({ text: n, value: n }));
      const trailing = [
        { text: "Lat", value: "lat" },
        { text: "Lng", value: "lng" },
        { text: "Last Update", value: "lastUpdate" },
      ];
      return [...fixed, ...sensors, ...trailing];
    },
    tableItems() {
      return this.filteredStations.map((station) => {
        const row = {
          _id: station._id,
          name: station.name,
          gatewaySerial: station.gatewaySerial,
          statusOnline: station.statusOnline,
          pump: station.pump,
          engine: station.engine,
          lat: station.GPS ? station.GPS.lat : undefined,
          lng: station.GPS ? station.GPS.lng : undefined,
          lastUpdate: station.updatedAt,
        };
        (station.status || []).forEach((s) => {
          if (s && s.name) row[s.name] = s.value;
        });
        return row;
      });
    },
  },
  mounted() {
    this.fetchStations();
  },
  methods: {
    async fetchStations() {
      this.loading = true;
      await this.$store.dispatch("getStations");
      await this.$store.dispatch("getTypes");
      this.loading = false;
    },
    clearRecentEvents() {
      this.$store.commit("clearRecentEvents");
    },
    pumpModeColor(mode) {
      if (mode === "Eco Mode") return "green";
      if (mode === "Normal Mode") return "blue";
      if (mode === "Sport Mode") return "orange";
      return "grey";
    },
    formatDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      return d.toLocaleString("th-TH", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
::v-deep .sticky-cols table th:nth-child(1),
::v-deep .sticky-cols table td:nth-child(1) {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
}
::v-deep .sticky-cols table th:nth-child(2),
::v-deep .sticky-cols table td:nth-child(2) {
  position: sticky;
  left: 180px;
  z-index: 2;
  background: #fff;
}
::v-deep .sticky-cols table th:nth-child(3),
::v-deep .sticky-cols table td:nth-child(3) {
  position: sticky;
  left: 340px;
  z-index: 2;
  background: #fff;
  box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.25);
}
::v-deep .sticky-cols table thead th:nth-child(1),
::v-deep .sticky-cols table thead th:nth-child(2),
::v-deep .sticky-cols table thead th:nth-child(3) {
  z-index: 3;
}
</style>
